import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Grid,
  Header,
  Image,
  Message,
  Card,
  Divider,
  Checkbox,
} from 'semantic-ui-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/User';
import {
  API,
  getLogo,
  showError,
  showInfo,
  showSuccess,
} from '../helpers';
import Turnstile from 'react-turnstile';
import { SUPPORT_EMAIL } from '../constants/support';
import './AuthReality.css';

const RegisterForm = () => {
  const { t } = useTranslation();
  const [, userDispatch] = useContext(UserContext);
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    password2: '',
    email: '',
    verification_code: '',
  });
  const { username, password, password2 } = inputs;
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const logo = getLogo();
  const location = useLocation();
  let affCode = new URLSearchParams(window.location.search).get('aff');
  if (affCode) {
    localStorage.setItem('aff', affCode);
  }

  useEffect(() => {
    let status = localStorage.getItem('status');
    if (status) {
      status = JSON.parse(status);
      setShowEmailVerification(status.email_verification);
      if (status.turnstile_check) {
        setTurnstileEnabled(true);
        setTurnstileSiteKey(status.turnstile_site_key);
      }
    }
  });

  useEffect(() => {
    let countdownInterval = null;
    if (disableButton && countdown > 0) {
      countdownInterval = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setDisableButton(false);
      setCountdown(30);
    }
    return () => clearInterval(countdownInterval);
  }, [disableButton, countdown]);

  let navigate = useNavigate();

  function handleChange(e) {
    const { name, value } = e.target;
    console.log(name, value);
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  }

  async function handleSubmit(e) {
    if (!agreeTerms) {
      showInfo(t('auth.register.agree_terms_required'));
      return;
    }
    if (username.length > 32) {
      showInfo(t('auth.register.username_too_long'));
      return;
    }
    if (password.length < 8) {
      showInfo(t('messages.error.password_length'));
      return;
    }
    if (password !== password2) {
      showInfo(t('messages.error.password_mismatch'));
      return;
    }
    if (username && password) {
      if (turnstileEnabled && turnstileToken === '') {
        showInfo(t('messages.error.turnstile_wait'));
        return;
      }
      setLoading(true);
      if (!affCode) {
        affCode = localStorage.getItem('aff');
      }
      inputs.aff_code = affCode;
      const res = await API.post(
        `/api/user/register?turnstile=${turnstileToken}`,
        inputs
      );
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('messages.success.register'));
        const loginRes = await API.post(`/api/user/login`, {
          username: inputs.username,
          password: inputs.password,
        });
        const loginPayload = loginRes.data;
        if (loginPayload.success && loginPayload.data) {
          userDispatch({ type: 'login', payload: loginPayload.data });
          localStorage.setItem('user', JSON.stringify(loginPayload.data));
          navigate('/');
        } else {
          showError(
            loginPayload.message ||
              'Automatic sign-in failed. Please sign in manually.'
          );
          navigate(`/login${location.search}`);
        }
      } else {
        showError(message);
      }
      setLoading(false);
    }
  }

  const sendVerificationCode = async () => {
    if (inputs.email === '') return;
    if (turnstileEnabled && turnstileToken === '') {
      showInfo(t('messages.error.turnstile_wait'));
      return;
    }
    setDisableButton(true);
    setLoading(true);
    const res = await API.get(
      `/api/verification?email=${inputs.email}&turnstile=${turnstileToken}`
    );
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('messages.success.verification_code'));
    } else {
      showError(message);
      setDisableButton(false);
      setCountdown(30);
    }
    setLoading(false);
  };

  return (
    <Grid textAlign='center' className='reality-auth-grid'>
      <Grid.Column style={{ maxWidth: 450 }}>
        <Card fluid className='chart-card reality-auth-card reality-auth-card--register'>
          <Card.Content>
            <Card.Header>
              <Header
                as='h2'
                textAlign='center'
                style={{ marginBottom: '0.75em' }}
              >
                <Image src={logo} style={{ marginBottom: '10px' }} />
                <Header.Content>{t('auth.register.title')}</Header.Content>
              </Header>
              <p className='reality-auth-trial-hint'>
                {t('auth.register.trial_hint')}
              </p>
            </Card.Header>
            <Form size='large'>
              <Form.Input
                fluid
                icon='user'
                iconPosition='left'
                placeholder={t('auth.register.username')}
                onChange={handleChange}
                name='username'
                maxLength={32}
                style={{ marginBottom: '1em' }}
              />
              <Form.Input
                fluid
                icon='lock'
                iconPosition='left'
                placeholder={t('auth.register.password')}
                onChange={handleChange}
                name='password'
                type='password'
                style={{ marginBottom: '1em' }}
              />
              <Form.Input
                fluid
                icon='lock'
                iconPosition='left'
                placeholder={t('auth.register.confirm_password')}
                onChange={handleChange}
                name='password2'
                type='password'
                style={{ marginBottom: '1em' }}
              />

              {showEmailVerification && (
                <>
                  <Form.Input
                    fluid
                    icon='mail'
                    iconPosition='left'
                    placeholder={t('auth.register.email')}
                    onChange={handleChange}
                    name='email'
                    type='email'
                    action={
                      <Button
                        onClick={sendVerificationCode}
                        disabled={loading}
                        className='reality-auth-secondary-btn'
                      >
                        {disableButton
                          ? t('auth.register.get_code_retry', { countdown })
                          : t('auth.register.get_code')}
                      </Button>
                    }
                    style={{ marginBottom: '1em' }}
                  />
                  <Form.Input
                    fluid
                    icon='lock'
                    iconPosition='left'
                    placeholder={t('auth.register.verification_code')}
                    onChange={handleChange}
                    name='verification_code'
                    style={{ marginBottom: '1em' }}
                  />
                </>
              )}

              {turnstileEnabled && (
                <div
                  style={{
                    marginBottom: '1em',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Turnstile
                    sitekey={turnstileSiteKey}
                    onVerify={(token) => {
                      setTurnstileToken(token);
                    }}
                  />
                </div>
              )}

              <Form.Field style={{ marginBottom: '1em', textAlign: 'left' }}>
                <Checkbox
                  checked={agreeTerms}
                  onChange={() => setAgreeTerms(!agreeTerms)}
                  label={
                    <label style={{ color: 'rgba(255,255,255,0.85)' }}>
                      {t('auth.register.agree_terms_prefix')}
                      <Link to='/terms' style={{ color: '#00ff85' }}>
                        {t('auth.register.agree_terms_link_terms')}
                      </Link>
                      {t('auth.register.agree_terms_middle')}
                      <Link to='/privacy' style={{ color: '#00ff85' }}>
                        {t('auth.register.agree_terms_link_privacy')}
                      </Link>
                      {t('auth.register.agree_terms_suffix')}
                    </label>
                  }
                />
              </Form.Field>

              <Button
                fluid
                size='large'
                onClick={handleSubmit}
                className='reality-auth-submit'
                loading={loading}
              >
                {t('auth.register.button')}
              </Button>
            </Form>

            <Divider />
            <Message className='reality-auth-message'>
              <div
                style={{
                  textAlign: 'center',
                  fontSize: '0.9em',
                  color: 'rgba(255,255,255,0.62)',
                }}
              >
                {t('auth.register.has_account')}
                <Link
                  to={`/login${location.search}`}
                  style={{ color: '#00ff85', marginLeft: '2px' }}
                >
                  {t('auth.register.login')}
                </Link>
              </div>
            </Message>
            <div
              style={{
                marginTop: '10px',
                fontSize: '0.85em',
                color: 'rgba(255,255,255,0.55)',
                textAlign: 'center',
              }}
            >
              {t('auth.register.enterprise_support')}{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#00ff85' }}>
                {SUPPORT_EMAIL}
              </a>
            </div>
          </Card.Content>
        </Card>
      </Grid.Column>
    </Grid>
  );
};

export default RegisterForm;
